import React, { useEffect, useState } from 'react';
import api from '../api/client';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBoxOpen, faLeaf, faFlask, faBox, faGift, faBagShopping, faBoxesStacked,
    faEnvelope, faScrewdriverWrench, faDroplet, faSeedling,
    faTriangleExclamation, faCircleExclamation, faCircleCheck, faPenToSquare,
    faDownload, faArrowDown, faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import '../low-stock-report.css';

const VISIBLE_ITEMS_LIMIT = 5;

const GROUP_ICONS = {
    set: faBoxOpen,
    wildmicr: faLeaf,
    fert_product: faFlask,
    readyproducts: faBox,
    kit: faGift,
    masterbag: faBagShopping,
    smallbag: faBoxesStacked,
    individualpacket: faEnvelope,
    wildmicrmasterbag: faLeaf,
    minitools: faScrewdriverWrench,
    silicagel: faDroplet,
    seeds: faSeedling,
    fert_raw: faFlask,
};

function severity(stock, threshold) {
    if (threshold <= 0) return 'critical';
    const ratio = stock / threshold;
    return ratio <= 0.5 ? 'critical' : 'warning';
}

function ThresholdEditor({ group, onSave }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(group.threshold);

    useEffect(() => { setValue(group.threshold); }, [group.threshold]);

    if (!editing) {
        return (
            <button
                type="button"
                className="low-stock-report__threshold-btn"
                onClick={() => setEditing(true)}
                title="Edit threshold"
            >
                <span>below {group.threshold} {group.unit}</span>
                <FontAwesomeIcon icon={faPenToSquare} className="low-stock-report__edit-icon" />
            </button>
        );
    }

    const commit = () => {
        setEditing(false);
        const num = parseFloat(value);
        if (!Number.isNaN(num) && num >= 0 && num !== group.threshold) {
            onSave(group.group_key, num);
        }
    };

    return (
        <input
            type="number"
            min="0"
            step="0.1"
            className="low-stock-report__threshold-input"
            value={value}
            autoFocus
            onChange={e => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        />
    );
}

function GroupRow({ group, onSaveThreshold }) {
    const icon = GROUP_ICONS[group.group_key] || faBox;
    const hasLowItems = group.items.length > 0;
    const [expanded, setExpanded] = useState(false);

    const hiddenCount = group.items.length - VISIBLE_ITEMS_LIMIT;
    const visibleItems = expanded ? group.items : group.items.slice(0, VISIBLE_ITEMS_LIMIT);

    return (
        <div className={`low-stock-report__group ${hasLowItems ? 'is-low' : ''}`}>
            <div className="low-stock-report__group-header">
                <span className="low-stock-report__group-icon-chip">
                    <FontAwesomeIcon icon={icon} />
                </span>
                <span className="low-stock-report__group-label">{group.label}</span>
                {hasLowItems && (
                    <span className="low-stock-report__group-count">{group.items.length}</span>
                )}
                <ThresholdEditor group={group} onSave={onSaveThreshold} />
            </div>

            {hasLowItems ? (
                <>
                    <ul className="low-stock-report__items">
                        {visibleItems.map((item, i) => {
                            const level = severity(item.stock, group.threshold);
                            const fillPct = group.threshold > 0
                                ? Math.min(100, Math.max(4, (item.stock / group.threshold) * 100))
                                : 4;
                            return (
                                <li key={i} className={`low-stock-report__item low-stock-report__item--${level}`}>
                                    <FontAwesomeIcon
                                        icon={level === 'critical' ? faTriangleExclamation : faCircleExclamation}
                                        className="low-stock-report__warning-icon"
                                    />
                                    <span className="low-stock-report__item-name">{item.name}</span>
                                    <span className="low-stock-report__item-bar">
                                        <span
                                            className={`low-stock-report__item-bar-fill low-stock-report__item-bar-fill--${level}`}
                                            style={{ width: `${fillPct}%` }}
                                        />
                                    </span>
                                    <span className="low-stock-report__item-stock">{item.stock} {group.unit}</span>
                                </li>
                            );
                        })}
                    </ul>
                    {hiddenCount > 0 && (
                        <button
                            type="button"
                            className="low-stock-report__toggle-btn"
                            onClick={() => setExpanded(e => !e)}
                        >
                            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
                        </button>
                    )}
                </>
            ) : (
                <div className="low-stock-report__ok">
                    <FontAwesomeIcon icon={faCircleCheck} className="low-stock-report__ok-icon" />
                    All good
                </div>
            )}
        </div>
    );
}

function exportToExcel(categories) {
    const rows = [['Category', 'Group', 'Item', 'Stock', 'Unit', 'Threshold']];
    categories.forEach(cat => {
        cat.groups.forEach(group => {
            group.items.forEach(item => {
                rows.push([cat.label, group.label, item.name, item.stock, group.unit, group.threshold]);
            });
        });
    });
    if (rows.length === 1) rows.push(['—', 'Nothing is currently below threshold', '', '', '', '']);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 32 }, { wch: 10 }, { wch: 8 }, { wch: 10 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Low Stock');
    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `low_stock_alerts_${today}.xlsx`);
}

function LowStockReport() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = () => {
        api.get('/api/stock-alerts')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => setCategories([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchData(); }, []);

    const handleSaveThreshold = (group_key, low_threshold) => {
        api.put('/api/stock-alerts/threshold', { group_key, low_threshold })
            .then(fetchData)
            .catch(() => {});
    };

    if (loading) return null;

    const totalLow = categories.reduce(
        (sum, cat) => sum + cat.groups.reduce((s, g) => s + g.items.length, 0), 0
    );

    return (
        <div className="low-stock-report">
            <div className="low-stock-report__header">
                <div className="low-stock-report__title-row">
                    <h2 className="low-stock-report__title">Low Stock Alerts</h2>
                    {totalLow > 0 ? (
                        <span className="low-stock-report__total-badge low-stock-report__total-badge--alert">
                            {totalLow} {totalLow === 1 ? 'item' : 'items'} low
                        </span>
                    ) : (
                        <span className="low-stock-report__total-badge low-stock-report__total-badge--ok">
                            <FontAwesomeIcon icon={faCircleCheck} /> All clear
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    className="low-stock-report__export-btn"
                    onClick={() => exportToExcel(categories)}
                >
                    <FontAwesomeIcon icon={faDownload} />
                    Export report
                </button>
            </div>

            <div className="low-stock-report__categories">
                {categories.map(cat => {
                    const catLowCount = cat.groups.reduce((s, g) => s + g.items.length, 0);
                    return (
                        <div key={cat.category} className="low-stock-report__category">
                            <div className="low-stock-report__category-header">
                                <h3 className="low-stock-report__category-title">{cat.label}</h3>
                                {catLowCount > 0 && (
                                    <span className="low-stock-report__category-badge">
                                        <FontAwesomeIcon icon={faArrowDown} />
                                        {catLowCount}
                                    </span>
                                )}
                            </div>
                            {cat.groups.map(group => (
                                <GroupRow key={group.group_key} group={group} onSaveThreshold={handleSaveThreshold} />
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default LowStockReport;
